import dynamic from 'next/dynamic';

import { Breadcrumbs } from '@natu/ui/Breadcrumbs';
import { paths } from '@natu/utils/paths';

import { BlokItem, sbEditable, SBProps, StoryblokAsset } from '../../../../../utils';
import { ArticleAuthorCopyToClipboardButton } from './components/ArticleAuthorCopyToClipboardButton';
import ArticleAuthorDecorator from './components/ArticleAuthorDecorator';

const DynamicRender = dynamic(() => import('../../../../../utils').then(mod => mod.DynamicRender));

const DynamicIcon = dynamic(() => import('@natu/ui/DynamicIcon').then(mod => mod.DynamicIcon));

interface SBArticleAuthorProps {
  slug?: string;
  name: string;
  position: string;
  description?: string;
  linkedInUrl?: string;
  image: StoryblokAsset;
  body?: BlokItem[];
  parentProps: Record<string, string>;
  advancedSeo?: BlokItem[];
}

export const SBArticleAuthor = ({ blok }: SBProps<SBArticleAuthorProps>) => {
  const { body, description, name, position, parentProps, linkedInUrl, advancedSeo } = blok;

  return (
    <>
      {advancedSeo && <DynamicRender data={advancedSeo} parentProps={parentProps} />}
      <main {...sbEditable(blok)}>
        <div className="bg-naturaily-azureGray pb-16 pt-6 lg:pb-24">
          <div className="mx-auto max-w-screen-xl px-4 md:px-6 lg:px-8">
            <Breadcrumbs
              className="mb-16"
              items={[
                { link: { href: paths.homepage }, title: 'Naturaily' },
                { link: { href: paths.blog }, title: 'Blog' },
                { title: name },
              ]}
            />
            <div className="lg:grid lg:grid-cols-[3fr_2fr] lg:items-end lg:gap-x-8">
              <div className="flex flex-col gap-y-4">
                <h1 className="text-display-sm-28 xl:text-display-lg-46 font-semibold text-gray-900">
                  {name}
                </h1>
                <p className="text-base text-gray-600 xl:text-xl">{position}</p>
                {description && <p className="text-sm text-gray-600 xl:text-lg">{description}</p>}
                <div className="flex items-center gap-4">
                  <ArticleAuthorCopyToClipboardButton />
                  {linkedInUrl && (
                    <a
                      href={linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shadow-xs flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white"
                      aria-label="Article author&#39;s LinkedIn page"
                    >
                      <DynamicIcon icon="linkedin" className="h-auto w-4 text-gray-400" />
                    </a>
                  )}
                </div>
              </div>
              <div className="hidden lg:flex lg:justify-self-end">
                <ArticleAuthorDecorator />
              </div>
            </div>
          </div>
        </div>
        <DynamicRender data={body} parentProps={parentProps} />
      </main>
    </>
  );
};
